import * as React from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  FormHelperText,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { styled } from "@mui/material/styles";
import { colors } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import {
  useLoginMutation,
  useRegisterAsCompanyMutation,
} from "../../services/auth.service";
import { Navigate, useNavigate } from "react-router-dom";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { isProductionMode } from "../../utils/helper";
import CornerRibbon from "../../components/CornerRibbon";
import workz360 from "../../images/workz360.png";
import { useGetBusinessVerticalsQuery } from "../../services/vertical.service";
import { toast } from "react-toastify";
import SelectCountry from "../../components/SelectCountry";
import SelectVertical from "../../components/SelectVertical";
import { geocode } from "../../utils/mapHelper";
import { StyledTextField, CardBase } from "../../components/global";

const StyledLoadingButton = styled(LoadingButton)(({ theme }) => ({
  borderRadius: 12,
  padding: "12px 24px",
  fontSize: "1rem",
  fontWeight: 600,
  textTransform: "none",
  backgroundColor: "#12903C",
  color: "#ffffff",
  boxShadow: "0 4px 14px rgba(18, 144, 60, 0.4)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: "#0f7a32",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(18, 144, 60, 0.6)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "10px 20px",
    fontSize: "0.875rem",
  },
}));

function Copyright(props: any) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      sx={{ opacity: 0.7, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
      {...props}
    >
      {"Copyright © "}
      <Link
        color="inherit"
        href="https://workz360.com/"
        sx={{
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        Workz360
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

function LoginLink(props: any) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
      {...props}
    >
      {"Already have a Workz360 account? "}
      <Link
        variant="body2"
        href="/login"
        sx={{
          fontWeight: 600,
          textDecoration: "none",
          color: "primary.main",
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        Log in
      </Link>
      {"."}
    </Typography>
  );
}

export default function SignupCompanyPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = React.useState(false);
  const [doRegister, { isLoading }] = useRegisterAsCompanyMutation();
  const isProduction = React.useMemo(() => isProductionMode(), []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    if (payload.password != payload.confirm_password) {
      toast.error("Password and confirm password doesnt match!");
      return;
    }

    setSubmitting(true);
    // locate on map
    const address = `${payload.city}, ${payload.state} ${payload.zip}`;
    try {
      const resp = await geocode(address);
      if (resp.data.status != "OK") {
        toast.error("Unknown address");
        return;
      }
      const first = resp.data.results[0];
      const { lat, lng } = first.geometry.location;
      payload.lat = lat;
      payload.lng = lng;
    } catch (err: any) {
      toast.error(err.message);
      return;
    }

    //sanitize
    delete payload.confirm_password;
    delete payload.agree;
    payload.billing_country = payload.country;
    payload.billing_city = payload.city;
    payload.billing_state = payload.state;
    payload.billing_zip = payload.zip;
    payload.billing_email = payload.email;
    payload.primary_contact_name =
      `${payload.first_name} ${payload.last_name}`.trim();
    payload.primary_contact_phone_number = payload.phone_number;
    payload.primary_contact_email = payload.email;

    try {
      await doRegister(payload).unwrap();
      setSubmitting(false);

      navigate("/signup/completed", { replace: true });
    } catch (err: any) {
      setSubmitting(false);
      toast.error(err.message);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        height: { xs: "100%", sm: "100vh" },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 3, sm: 4 },
        px: { xs: 2, sm: 3 },
        backgroundImage: "url('/background-home.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: { xs: "scroll", sm: "fixed" },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", sm: 600 },
          position: "relative",
        }}
      >
        <Box sx={{ position: "relative" }}>
          {!isProduction && (
            <CornerRibbon
              position="top-right"
              backgroundColor={colors.yellow[200]}
              fontColor="#333"
            >
              STAGING
            </CornerRibbon>
          )}
          <CardBase
            useGlassCard={true}
            nested={false}
            sx={{
              p: { xs: 2.5, sm: 4, md: 5 },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                mb: { xs: 2, sm: 3 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: { xs: 0.75, sm: 1 },
              }}
            >
              <Box
                component="img"
                src={workz360}
                alt="Workz360"
                sx={{
                  height: { xs: 44, sm: 56 },
                  width: "auto",
                  objectFit: "contain",
                  filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))",
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  letterSpacing: 0.5,
                  mt: { xs: 0.5, sm: 1 },
                  textAlign: "center",
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                }}
              >
                Register as Company
              </Typography>
            </Box>
            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit(onSubmit)}
              sx={{ mt: { xs: 0.75, sm: 1 } }}
            >
              <Controller
                name="name"
                defaultValue=""
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, onBlur } }) => (
                  <StyledTextField
                    fullWidth
                    size={isMobile ? "small" : "medium"}
                    label="Company Name"
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!errors.name}
                    helperText={!!errors.name && "This field is required"}
                  />
                )}
              />
              <Controller
                name="business_vertical"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <SelectVertical
                    onChange={onChange}
                    value={value}
                    size={isMobile ? "small" : "medium"}
                    sx={{ mt: 1 }}
                    error={!!errors.business_vertical}
                    helperText={
                      !!errors.business_vertical && "This field is required"
                    }
                  />
                )}
              />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 1 }}
              >
                <Controller
                  name="country"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <SelectCountry
                      onChange={onChange}
                      value={value}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        width: { xs: "100%", sm: "200px" },
                        flex: { xs: "none", sm: "0 0 200px" },
                        mt: 0,
                        "& > div": {
                          mt: 0,
                        },
                      }}
                      error={!!errors.country}
                      helperText={!!errors.country && "This field is required"}
                    />
                  )}
                />
                <Controller
                  name="state"
                  defaultValue=""
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur } }) => (
                    <StyledTextField
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      label="State"
                      sx={{ mt: 0 }}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.state}
                      helperText={!!errors.state && "This field is required"}
                    />
                  )}
                />
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 1 }}
              >
                <Controller
                  name="city"
                  defaultValue=""
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur } }) => (
                    <StyledTextField
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      label="City"
                      sx={{ mt: 0 }}
                      autoFocus
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.city}
                      helperText={!!errors.city && "This field is required"}
                    />
                  )}
                />
                <Controller
                  name="zip"
                  defaultValue=""
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur } }) => (
                    <StyledTextField
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      label="Zip Code"
                      sx={{ mt: 0 }}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.zip}
                      helperText={!!errors.zip && "This field is required"}
                    />
                  )}
                />
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 1 }}
              >
                <Controller
                  name="first_name"
                  defaultValue=""
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur } }) => (
                    <StyledTextField
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      label="First Name"
                      sx={{ mt: 0 }}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.first_name}
                      helperText={!!errors.first_name && "This field is required"}
                    />
                  )}
                />
                <Controller
                  name="last_name"
                  defaultValue=""
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur } }) => (
                    <StyledTextField
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      label="Last Name"
                      sx={{ mt: 0 }}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.last_name}
                      helperText={!!errors.last_name && "This field is required"}
                    />
                  )}
                />
                <Controller
                  name="phone_number"
                  defaultValue=""
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur } }) => (
                    <StyledTextField
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      label="Phone number"
                      sx={{ mt: 0 }}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.phone_number}
                      helperText={
                        !!errors.phone_number && "This field is required"
                      }
                    />
                  )}
                />
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 1 }}
              >
                <Controller
                  name="email"
                  defaultValue=""
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur } }) => (
                    <StyledTextField
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      label="Email Address"
                      sx={{ mt: 0 }}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.email}
                      helperText={!!errors.email && "This field is required"}
                    />
                  )}
                />
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 1 }}
              >
                <Controller
                  name="password"
                  defaultValue=""
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      fullWidth
                      label="Password"
                      type="password"
                      id="password"
                      size={isMobile ? "small" : "medium"}
                      sx={{ mt: 0 }}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.password}
                      helperText={!!errors.password && "This field is required"}
                    />
                  )}
                />
                <Controller
                  name="confirm_password"
                  defaultValue=""
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      fullWidth
                      label="Confirm Password"
                      type="password"
                      size={isMobile ? "small" : "medium"}
                      sx={{ mt: 0 }}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.confirm_password}
                      helperText={
                        !!errors.confirm_password && "This field is required"
                      }
                    />
                  )}
                />
              </Stack>
              <Controller
                name="agree"
                control={control}
                rules={{ required: true }}
                defaultValue={false}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      control={<Checkbox {...field} />}
                      label="I agree with Workz360's Terms and Agreements"
                    />
                    {errors.agree && (
                      <FormHelperText style={{ color: "red" }}>
                        This field is required
                      </FormHelperText>
                    )}
                  </>
                )}
              />
              <StyledLoadingButton
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 1.5, sm: 2 } }}
                loading={submitting}
              >
                Submit
              </StyledLoadingButton>
              <LoginLink sx={{ mt: { xs: 2, sm: 3 } }} />
              <Copyright sx={{ mt: { xs: 3, sm: 5 } }} />
            </Box>
          </CardBase>
        </Box>
      </Box>
    </Box>
  );
}
